import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import List from '../List';
import './styles.css';

const Wrapper = ({
  list,
  children,
  limit,
  minLength,
  suggestFormatted,
  onClickSuggest,
  addSuggestion,
  onChange,
  fetchData,
  onFocus,
  onBlur,
  onDirty,
  className,
  icon,
  focus: inputFocused,
  setInputValue,
  emptyText,
  inputName,
  inputValid,
  onListShow,
  ...props
}) => {
  const wrapperRef = useRef();
  const inputRef = useRef();
  const [hasSelected, setHasSelected] = useState();
  const [dirty, setDirty] = useState();
  const [focus, setFocus] = useState();
  const [value, setValue] = useState('');

  useOnClickOutside(
    wrapperRef,
    useCallback(() => {
      setFocus(false);
      setHasSelected(false);
    }, [])
  );

  useEffect(() => {
    if (inputFocused) inputRef.current.focus();
  }, [inputFocused]);

  useEffect(() => {
    if (setInputValue) setValue(setInputValue);
  }, [setInputValue]);

  const onChangeValue = e => {
    const { value: targetValue } = e.target;
    setValue(targetValue);
    setDirty(true);
    onChange(e);
    if (fetchData) fetchData(targetValue);
  };

  const onClickItem = item => {
    onClickSuggest({
      item,
      formatted: suggestFormatted(item).value,
    });
    setValue(suggestFormatted(item).value);
    setFocus(false);
    setHasSelected(true);
  };

  const onBlurInput = e => {
    onBlur(e);
    setDirty(true);
    onClickSuggest({});
  };

  const onFocusInput = e => {
    onFocus(e);
    setFocus(true);
  };

  const onClickInput = () => {
    setFocus(true);
  };

  useEffect(() => {
    if (dirty) {
      onDirty(true);
    }
  }, [dirty]);

  return (
    <div
      ref={wrapperRef}
      className={classNames(`AutoSuggest__Wrapper__root`, {
        'AutoSuggest__Wrapper__root--focused': focus,
        'AutoSuggest__Wrapper__root--hasSelected': hasSelected,
        [className]: className,
      })}
    >
      {icon && <i className={`AutoSuggest__Wrapper__icon ${icon}`} />}
      <input
        className={classNames('AutoSuggest__Wrapper__input', {
          [`${inputName}--valid--input`]: inputName && inputValid,
          [`${inputName}--input`]: inputName,
        })}
        required
        ref={inputRef}
        autoComplete="off"
        onChange={onChangeValue}
        onBlur={onBlurInput}
        onFocus={onFocusInput}
        onClick={onClickInput}
        minLength={minLength}
        value={value}
        {...props}
      />
      {children}
      {focus && value.length >= minLength && (
        <List
          data={list}
          suggestFormatted={suggestFormatted}
          addSuggestion={addSuggestion}
          onListShow={onListShow}
          onClickItem={onClickItem}
          limit={limit}
          emptyText={emptyText}
        />
      )}
    </div>
  );
};

Wrapper.propTypes = {
  suggestFormatted: PropTypes.func,
  onClickSuggest: PropTypes.func,
  addSuggestion: PropTypes.func,
  fetchData: PropTypes.func,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onListShow: PropTypes.func,
  onBlur: PropTypes.func,
  onDirty: PropTypes.func,
  list: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string])
  ),
  children: PropTypes.node,
  limit: PropTypes.number,
  minLength: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  icon: PropTypes.string,
  setInputValue: PropTypes.string,
};

Wrapper.defaultProps = {
  onClickSuggest: () => {},
  suggestFormatted: () => {},
  addSuggestion: () => {},
  onListShow: () => {},
  fetchData: undefined,
  onChange: () => {},
  onFocus: () => {},
  onBlur: () => {},
  onDirty: () => {},
  children: undefined,
  list: [],
  limit: 20,
  minLength: 0,
  className: undefined,
  icon: undefined,
  setInputValue: undefined,
};
export default Wrapper;
